// Create instance of Amplitude services in one location
// then export as context to be used where needed

// Can add common Amplitude actions to the class so they
// can be reused in multiple locations
import React from "react";
import amplitude from "amplitude-js";

export const AmplitudeContext = React.createContext(undefined);

class Amplitude {
  constructor() {
    // The first statement will kick us out of this if window is undefined so we won't throw
    // an error on the second check.
    if(typeof window !== 'undefined' && typeof window.navigator !== 'undefined'){
      amplitude.getInstance().init(process.env.GATSBY_AMPLITUDE_API_KEY);

      // This line is so we can edit our dev env file so we don't clutter the Amplitude dashboard
      // if we are not testing Amplitude events.
      this.windowAvailable = (process.env.GATSBY_FIRE_AMPLITUDE_EVENTS === 'true') ? true : false;
    } else {
      this.windowAvailable = false;
    }
  }

  ///
  /// This function will log a specific Amplitude event.
  ///
  /// (string) event [in] - the specific event we want to log.
  ///
  logEvent = async (event) => {
    if(this.windowAvailable){
      amplitude.getInstance().logEvent(event);
    }
  };

  ///
  /// Overrided function if the user passes event properties.
  ///
  /// (string) event [in] - the specific event we want to log.
  /// (array)  properties [in] - the array of properties we want to attribute.
  ///
  logEvent = async (event, properties) => {
    if(this.windowAvailable){
      amplitude.getInstance().logEvent(event, properties);
    }
  }

  ///
  /// This function will log errors to Amplitude.
  ///
  /// (string) pathToError [in] - the path to the file where the error occured
  /// (string) errorCode [in] - the error code the corresponds to the document in confluence
  ///
  logError = async (pathToError, errorCode) => {
    if(this.windowAvailable){
      amplitude.getInstance().logEvent("ERROR", {
        path: pathToError,
        errorCode: errorCode,
      });
    }
  }

  ///
  /// This function will set the users ID once we are able to get it.
  ///
  /// (string) userId [in] - the userId to set the current user to.
  ///
  setUser = async (userId) => {
    if(this.windowAvailable){
      amplitude.getInstance().setUserId(userId);
    }
  }

  ///
  /// This function will log the user out if they click log out.
  ///
  logUserOut = async () => {
    if(this.windowAvailable){
      amplitude.getInstance().setUserId(null);
    }
  }

  ///
  /// This function will set a user property to the given value.
  ///
  /// (string) key [in] - the user property we want to set.
  /// (string) value [in] - the value of the given user property.
  ///
  setUserProperty = async (key, value) => {
    if(typeof window !== undefined){
      const identify1 = new amplitude.Identify().set(key, value);
      amplitude.getInstance().identify(identify1);
    }
  }

  ///
  /// This function will set a user property ONCE.
  ///
  /// (string) key [in] - the user property we want to set.
  /// (string) value [in] - the value of the given user property.
  ///
  setUserPropertyOnce = async (key, value) => {
    const identify1 = new amplitude.Identify().setOnce(key, value);
    amplitude.getInstance().identify(identify1);
  }
}

const Provider = (props) => {
  return (
    <AmplitudeContext.Provider value={new Amplitude()}>
      {props.children}
    </AmplitudeContext.Provider>
  );
};

export default Provider;

